import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import { signInRecovery as enSignInRecovery, authErrors as enAuthErrors, passwordReset as enPasswordReset } from '../../public/locales/en/auth.json';
import { signInRecovery as fiSignInRecovery, authErrors as fiAuthErrors, passwordReset as fiPasswordReset } from '../../public/locales/fi/auth.json';
import { signInRecovery as svSignInRecovery, authErrors as svAuthErrors, passwordReset as svPasswordReset } from '../../public/locales/sv/auth.json';
import { signInRecovery as frSignInRecovery, authErrors as frAuthErrors, passwordReset as frPasswordReset } from '../../public/locales/fr/auth.json';

// Single source of truth for supported languages in login-ui. Translations
// load over HTTP from public/locales/{{lng}}/ — when adding a language, add
// its folder there and extend this list; everything else (i18next config,
// the LoginPage language switcher) derives from it.
export const SUPPORTED_LANGUAGES = ['en', 'fi', 'sv', 'fr', 'de'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * Native display name (autonym) for a language code, e.g. 'fi' -> 'Suomi'.
 * Uses Intl.DisplayNames so new languages need no hardcoded name table.
 */
export function languageDisplayName(code: string): string {
  try {
    const name = new Intl.DisplayNames([code], { type: 'language' }).of(code);
    if (name) return name.charAt(0).toLocaleUpperCase(code) + name.slice(1);
  } catch {
    // Unknown code — fall through to returning the code itself
  }
  return code;
}

// Initialize i18next
i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: [...SUPPORTED_LANGUAGES],
    debug: import.meta.env.DEV,

    interpolation: {
      escapeValue: false, // React already escapes values
    },

    detection: {
      order: ['querystring', 'localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupQuerystring: 'lng',
    },

    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },

    ns: ['auth', 'common', 'errors'],
    defaultNS: 'auth',

    // Keep inline resources when backend plugin is active
    partialBundledLanguages: true,

    resources: {
      en: {
        auth: {
          signInRecovery: enSignInRecovery,
          authErrors: enAuthErrors,
          login: {
            copyright: '© {{year}} Agion. All rights reserved.',
            headline: 'Agents, safely under your control.',
            subline: 'Build and govern trustworthy agent swarms that turn your data into intelligence — on your choice of cloud, models, and apps. Sovereignty, delivered.',
            sublineShort: 'Build and govern trustworthy agent swarms — on your cloud, models, and apps.',
            formHeading: 'Welcome back',
            formSubheading: 'Sign in to your Agion workspace.',
            showPassword: 'Show',
            hidePassword: 'Hide',
            newToAgion: 'New to Agion?',
            joinWaitlist: 'Join the waitlist',
            languageSwitch: 'Language',
            title: 'Sign in to your account',
            email: 'Email address',
            emailLabel: 'Email',
            emailPlaceholder: 'you@example.com',
            password: 'Password',
            passwordLabel: 'Password',
            remember: 'Remember me',
            submit: 'Sign in',
            forgotPassword: 'Forgot password?',
            createAccount: 'Create your account',
            noAccount: 'New to Agion?',
            orContinueWith: 'or continue with',
            signingIn: 'Signing in...',
            googleSignIn: 'Sign in with Google',
            microsoftSignIn: 'Sign in with Microsoft',
            allRightsReserved: 'All rights reserved.',
            missingRedirect: 'Login response missing redirect URL',
            genericError: 'An error occurred during login',
            googleError: 'Failed to initiate Google login',
            microsoftError: 'Failed to initiate Microsoft login',
          },
          register: {
            title: 'Create your account',
            firstName: 'First name',
            lastName: 'Last name',
            email: 'Email address',
            password: 'Password',
            confirmPassword: 'Confirm password',
            terms: 'I accept the Terms of Service and Privacy Policy',
            submit: 'Create account',
            alreadyHaveAccount: 'Already have an account?',
            signIn: 'Sign in',
            creating: 'Creating account...',
            heading: 'Create Account',
            subheading: 'Join Agion Platform today',
            goToLogin: 'Go to login page',
            firstNamePlaceholder: 'John',
            lastNamePlaceholder: 'Doe',
            emailPlaceholder: 'john@company.com',
            passwordPlaceholder: 'Create a password',
            confirmPasswordPlaceholder: 'Re-enter password',
            acceptTermsLead: 'I accept the',
            termsOfService: 'Terms of Service',
            acceptTermsJoin: 'and',
            privacyPolicy: 'Privacy Policy',
            submitButton: 'Create Account',
            creatingButton: 'Creating Account...',
          },
          organizationSignup: {
            title: 'Create Organization',
            description: 'Your organization doesn\'t exist yet. Please create one to continue.',
            namePlaceholder: 'Acme Corporation',
            creating: 'Creating Organization...',
            submit: 'Create Organization',
            backToLogin: 'Back to Login',
          },
          mfa: {
            title: 'Two-factor authentication',
            description: 'Enter the verification code from your authenticator app',
            code: 'Verification code',
            submit: 'Verify',
            backupCode: 'Use backup code',
            havingTrouble: 'Having trouble?',
            verifying: 'Verifying...',
            methods: {
              totp: 'Authenticator app',
              webauthn: 'Security key',
              sms: 'Text message',
              email: 'Email',
              backup: 'Backup code',
            },
          },
          consent: {
            title: 'Authorize {{client}}',
            description: '{{client}} is requesting access to your account',
            defaultDescription: 'This application is requesting access to your account',
            permissions: 'This application will be able to:',
            permissionsTitle: 'This application will be able to:',
            userInfo: 'Information shared:',
            remember: 'Remember my decision',
            allow: 'Allow',
            deny: 'Deny',
            allowing: 'Authorizing...',
            invalid: 'Invalid Consent Request',
            invalidDescription: 'The consent request is invalid or has expired.',
            required: 'Required',
            loggedInAs: 'Signed in as {{email}}',
            website: 'Website',
            privacy: 'Privacy Policy',
            terms: 'Terms of Service',
            scopes: {
              openid: 'Verify your identity',
              openidDesc: 'Access your unique user identifier',
              profile: 'View your profile',
              profileDesc: 'Access your name and profile information',
              email: 'View your email',
              emailDesc: 'Access your email address',
              phone: 'View your phone number',
              phoneDesc: 'Access your phone number',
              address: 'View your address',
              addressDesc: 'Access your postal address',
              offlineAccess: 'Stay signed in',
              offlineAccessDesc: 'Maintain access when you are not actively using the application',
              customDesc: 'Access to {{scope}}',
            },
          },
          forgotPassword: {
            title: 'Reset your password',
            description: 'Enter your email address and we\'ll send you a link to reset your password',
            email: 'Email address',
            submit: 'Send reset link',
            backToLogin: 'Back to sign in',
            sending: 'Sending...',
          },
          resetPassword: {
            title: 'Set new password',
            description: 'Enter your new password below',
            password: 'New password',
            confirmPassword: 'Confirm new password',
            submit: 'Reset password',
            resetting: 'Resetting...',
          },
          emailVerification: {
            title: 'Check Your Email',
            description: 'We\'ve sent a verification link to your email address. Please click the link to verify your account and continue.',
            descriptionWithEmail: 'We\'ve sent a verification link to {{email}}. Please click the link to verify your account and continue.',
            expiryWarning: 'The verification link will expire in 30 minutes.',
            checkSpam: 'Don\'t see the email? Check your spam or junk folder.',
            didntReceive: 'Didn\'t receive the email?',
            resendButton: 'Resend Verification Email',
            resendSuccess: 'Verification email sent successfully! The new link will expire in 30 minutes.',
            resendError: 'Failed to resend verification email',
            successTitle: 'Email Verified!',
            successMessage: 'Your email has been verified successfully.',
            verifiedDomain: 'Verified domain',
            signInToCreateOrg: 'Sign in to set up your organization.',
            signIn: 'Sign In',
          },
          device: {
            title: 'Device Authorization',
            description: 'Enter the code shown on your device to sign in.',
            codeLabel: 'Device code',
            codeHint: 'Enter the code displayed in your terminal',
            continue: 'Continue',
            verifying: 'Verifying code...',
            confirmTitle: 'Authorize Device',
            confirmDescription: 'A device is requesting access to your account.',
            requestingApp: 'Application',
            permissions: 'Requested permissions',
            approve: 'Approve',
            deny: 'Deny',
            securityWarning: 'Only approve if you initiated this request.',
            successTitle: 'Device Authorized',
            successDescription: 'Your device has been authorized. You can return to your terminal.',
            closeWindow: 'You may close this window.',
            deniedTitle: 'Authorization Denied',
            deniedDescription: 'The device authorization request was denied.',
            errorTitle: 'Error',
            genericError: 'Something went wrong. Please try again.',
            tryAgain: 'Try again',
            invalidCode: 'Invalid or expired code. Please check and try again.',
            verifyError: 'Failed to verify code. Please try again.',
            approveError: 'Failed to authorize device. Please try again.',
            denyError: 'Failed to deny request. Please try again.',
            scopes: {
              openid: 'Verify your identity',
              profile: 'View your name and profile',
              email: 'View your email address',
              offlineAccess: 'Stay signed in',
            },
          },
          authorize: {
            title: 'Authorization Request',
            description: '{{app}} is requesting access to your account',
            authorizing: 'Authorizing...',
            errorTitle: 'Authorization Error',
            invalidRequest: 'Invalid authorization request. Missing required parameters.',
            invalidRedirectUri: 'Invalid redirect URI for this client.',
            processingError: 'Failed to process authorization request.',
            authorizationFailed: 'Authorization failed',
            invalidServerResponse: 'Invalid response from authorization server',
            redirectMismatch: 'Invalid redirect URL from server - does not match registered redirect_uri',
            permissionsTitle: 'This application will be able to:',
            secureTitle: 'Secure Authorization',
            secureDescription: 'This authorization uses industry-standard OAuth 2.0 with PKCE for enhanced security.',
            approve: 'Authorize',
            deny: 'Deny',
            defaultUser: 'User',
            footerConsent: 'By authorizing, you allow {{app}} to access your Agion account on your behalf.',
            footerRevoke: 'You can revoke this access at any time from your account settings.',
          },
          layout: {
            privacyPolicy: 'Privacy Policy',
            termsOfService: 'Terms of Service',
            support: 'Support',
            skipToContent: 'Skip to main content',
            allRightsReserved: 'All rights reserved.',
          },
          backToLogin: 'Back to Sign In',
          waitlist: {
            checkEmailTitle: 'Check your email',
            checkEmailExpiry: 'The link expires in 24 hours.',
            signedInAs: 'Signed in as',
            loading: 'Loading',
            emailInvalidWork: 'Enter a valid work email.',
            requestAccessTitle: 'Request access',
            requestAccessSubtitle: 'Agion onboards organizations in waves. Tell us where you work and we’ll be in touch.',
            requestAccessButton: 'Request access',
            orRequestWith: 'or request access with',
            oneMoreStepTitle: 'One more step',
            oneMoreStepSubtitle: 'Add your organization name to finish, then continue with the provider you used.',
            continueWithGoogle: 'Continue with Google',
            continueWithMicrosoft: 'Continue with Microsoft',
            checkEmailMessageRich: 'We sent a verification link and code to <b>{{email}}</b>. Click the link, or enter the code below.',
            codeLabel: 'Verification code',
            codePlaceholder: '123456',
            codeHint: 'Enter the 6-digit code from the email. It expires in 15 minutes.',
            codeSubmit: 'Continue',
            codeInvalid: 'That code is not valid or has expired. Check the email, or request a new one.',
            didntGetEmail: 'Didn’t get the email?',
            onListTitle: 'You’re on the list',
            onListMessage: 'Thanks for your interest in Agion. We’ll email you as soon as access opens for your organization.',
            onListInbox: 'Check your inbox for updates.',
            setPasswordTitle: 'Set your password',
            setPasswordSubtitle: 'Confirm your details and choose a password to finish joining.',
            workEmailLabel: 'Work email',
            verifiedMarker: 'Verified',
            passwordWeak: 'Weak password',
            passwordGood: 'Good password',
            passwordStrong: 'Strong password',
            showPassword: 'Show',
            hidePassword: 'Hide',
            passwordsDontMatch: 'Passwords don’t match.',
            passwordsMatch: 'Passwords match',
            confirmJoinButton: 'Confirm & join',
            pendingApprovalTitle: 'Request pending approval',
            pendingApprovalBodyRich: 'Your organization is already on Agion. We’ve sent your request to join <b>{{organization}}</b> to its administrators.',
            pendingApprovalBodyGeneric: 'Your organization is already on Agion. We’ve sent your request to join to its administrators.',
            whatHappensNext: 'What happens next',
            pendingStep1: 'An admin reviews your request to join',
            pendingStep2: 'You’re approved and added to the workspace',
            pendingStep3: 'You’ll get an email — then you can sign in',
            wrongOrg: 'Wrong organization? Contact',
            backToSignIn: 'Back to sign in',
            title: 'Join the Agion Waitlist',
            subtitle: "Run swarms of AI agents safely. Create an account to join our waitlist today.",
            emailLabel: 'Work email',
            emailPlaceholder: 'you@company.com',
            orgLabel: 'Organization name',
            orgPlaceholder: 'Your company or organization',
            joinButton: 'Join Waitlist',
            submitting: 'Submitting...',
            orSignUpWith: 'or sign up with',
            googleSignUp: 'Sign up with Google',
            microsoftSignUp: 'Sign up with Microsoft',
            hasAccount: 'Already have an account?',
            signIn: 'Sign in',
            blockedDomain: 'Please use your work email address',
            emailRequired: 'Email is required',
            emailInvalid: 'Please enter a valid email address',
            orgRequired: 'Organization name is required',
            genericError: 'An error occurred',
            successTitle: 'Thank you!',
            successMessage: "We've received your interest in Agion. We'll be in touch when access becomes available for your organization.",
            successInbox: 'Check your inbox for updates.',
            copyright: 'Agion. All rights reserved.',
            confirmTitle: 'Confirm Your Details',
            confirmSubtitle: 'Please verify your information before joining the waitlist.',
            confirmButton: 'Confirm & Join Waitlist',
            passwordLabel: 'Password',
            passwordPlaceholder: 'Create a password',
            confirmPasswordLabel: 'Confirm password',
            confirmPasswordPlaceholder: 'Re-enter password',
            passwordRequired: 'Password is required',
            passwordMismatch: 'Passwords do not match',
            linkExpiredTitle: 'Link expired',
            backToWaitlist: 'Back to request access',
            invalidRequest: 'Invalid request. Please try again.',
            tokenExpired: 'Invalid or expired token',
            didntReceiveEmail: "Didn't receive the email?",
            resendButton: 'Resend verification email',
            resendSuccess: 'Verification email sent! Check your inbox.',
            resendCooldown: 'Resend in {{seconds}}s',
            pendingTitle: 'Your account is pending approval',
            pendingMessage: 'We’ll notify you by email when your organization is ready. You’ll be able to sign in with the same account you used to join the waitlist.',
            pendingContact: 'Questions? Contact us at',
            supportEmail: 'support@agion.ai',
          },
          socialLogin: {
            google: 'Continue with Google',
            github: 'Continue with GitHub',
            microsoft: 'Continue with Microsoft',
            apple: 'Continue with Apple',
          },
          social: {
            google: 'Google',
            microsoft: 'Microsoft',
          },
          invite: {
            title: 'Join {{organization}}',
            titleDefault: 'Organization Invitation',
            subtitle: '{{inviter}} has invited you to join as {{role}}',
            subtitleDefault: 'You have been invited to join as {{role}}',
            processing: 'Processing your invitation...',
            redirectingToLogin: 'Redirecting you to login...',
            validating: 'Validating your invitation...',
            successAccepted: 'Invitation Accepted!',
            successRegistered: 'Registration Successful!',
            successJoined: "You've joined {{organization}}.",
            redirecting: 'Redirecting you to login...',
            errorTitle: 'Invitation Error',
            errorInvalidToken: 'This invitation link is invalid. Please check the link or request a new invitation.',
            errorExpired: 'This invitation has expired. Please request a new invitation.',
            errorAlreadyAccepted: 'This invitation has already been accepted.',
            errorNetwork: 'Failed to validate invitation. Please check your connection and try again.',
            errorGeneric: 'This invitation is invalid or has expired. Please request a new invitation.',
            errorNoToken: 'No invitation token provided. Please use the link from your invitation email.',
            needHelp: 'Need help?',
            contactSupport: 'Contact Support',
            wrongAccount: 'Wrong account?',
            switchAccount: 'Sign in with different account',
            hasAccount: 'Already have an account?',
            signInInstead: 'Sign in instead',
            email: 'Email',
            verificationCode: 'Verification Code',
            verificationCodePlaceholder: 'Enter 6-digit code',
            verificationCodeHint: 'Check your email for the 6-digit verification code',
            firstName: 'First Name',
            firstNamePlaceholder: 'First name',
            lastName: 'Last Name',
            lastNamePlaceholder: 'Last name',
            password: 'Password',
            passwordPlaceholder: 'Create a password',
            confirmPassword: 'Confirm Password',
            confirmPasswordPlaceholder: 'Confirm your password',
            acceptButton: 'Accept Invitation',
            accepting: 'Accepting...',
            createButton: 'Create Account & Join',
            creating: 'Creating Account...',
            orSignInWith: 'Or sign in with',
            orContinueWith: 'Or continue with',
            termsNotice: 'By creating an account, you agree to our',
            termsOfService: 'Terms of Service',
            privacyPolicy: 'Privacy Policy',
            goToLogin: 'Go to Login',
            validationCodeRequired: 'Please enter the 6-digit verification code from your invitation email.',
            validationNameRequired: 'First name and last name are required.',
            validationPasswordMatch: 'Passwords do not match.',
            acceptFailed: 'Failed to accept invitation. Please check your verification code.',
            registrationFailed: 'Registration failed. Please check your verification code and try again.',
            didntReceiveCode: "Didn't receive the code?",
            resendButton: 'Resend code',
            resendSuccess: 'A new verification code has been sent to your email.',
            resendError: 'Failed to resend code. Please try again.',
            resendCooldown: 'Resend in {{seconds}}s',
          },
          passwordReset: enPasswordReset,
          newPassword: {
            hint: 'At least {{min}} characters.',
            hintWithNumberOrSpecial: 'At least {{min}} characters, including a number or a special character.',
          },
          logout: {
            signingOut: 'Signing out...',
            error: 'Sign out failed',
            redirecting: 'Redirecting to login...',
          },
        },
        common: {
          loading: 'Loading...',
          error: 'Error',
          success: 'Success',
          retry: 'Try again',
          cancel: 'Cancel',
          continue: 'Continue',
          back: 'Back',
          next: 'Next',
          finish: 'Finish',
          close: 'Close',
          save: 'Save',
          email: 'Email',
          password: 'Password',
          name: 'Name',
          required: 'Required',
          optional: 'Optional',
        },
        errors: {
          invalidCredentials: 'Invalid email or password',
          accountLocked: 'Account locked. Please try again later.',
          networkError: 'Network error. Please check your connection.',
          sessionExpired: 'Your session has expired. Please sign in again.',
          invalidToken: 'Invalid or expired token',
          emailRequired: 'Email is required',
          passwordRequired: 'Password is required',
          passwordTooShort: 'Password must be at least 8 characters',
          passwordsDoNotMatch: 'Passwords do not match',
          invalidEmail: 'Please enter a valid email address',
          termsRequired: 'You must accept the terms and conditions',
          mfaRequired: 'Two-factor authentication is required',
          invalidMfaCode: 'Invalid verification code',
          rateLimited: 'Too many attempts. Please try again later.',
          serverError: 'Server error. Please try again later.',
          unknownError: 'An unexpected error occurred',
        },
      },
      fr: {
        auth: {
          signInRecovery: frSignInRecovery,
          authErrors: frAuthErrors,
          login: {
            copyright: '© {{year}} Agion. Tous droits réservés.',
            headline: 'Des agents, en toute sécurité sous votre contrôle.',
            subline: 'Créez et gouvernez des essaims d’agents fiables qui transforment vos données en intelligence — dans votre choix de cloud, de modèles et d’applications. La souveraineté, livrée.',
            sublineShort: 'Créez et gouvernez des essaims d’agents fiables — sur votre cloud, vos modèles et vos applications.',
            formHeading: 'Bon retour',
            formSubheading: 'Connectez-vous à votre espace Agion.',
            showPassword: 'Afficher',
            hidePassword: 'Masquer',
            newToAgion: 'Nouveau sur Agion ?',
            joinWaitlist: 'Rejoindre la liste d’attente',
            languageSwitch: 'Langue',
            title: 'Connectez-vous \u00e0 votre compte',
            email: 'Adresse e-mail',
            emailLabel: 'E-mail',
            emailPlaceholder: 'vous@exemple.com',
            password: 'Mot de passe',
            passwordLabel: 'Mot de passe',
            remember: 'Se souvenir de moi',
            submit: 'Se connecter',
            forgotPassword: 'Mot de passe oublié ?',
            createAccount: 'Cr\u00e9er votre compte',
            noAccount: 'Nouveau sur Agion\u2009?',
            orContinueWith: 'ou continuer avec',
            signingIn: 'Connexion en cours\u2026',
            googleSignIn: 'Se connecter avec Google',
            microsoftSignIn: 'Se connecter avec Microsoft',
            allRightsReserved: 'Tous droits r\u00e9serv\u00e9s.',
            missingRedirect: 'URL de redirection manquante dans la r\u00e9ponse de connexion',
            genericError: 'Une erreur est survenue lors de la connexion',
            googleError: 'Impossible d\u2019initier la connexion Google',
            microsoftError: 'Impossible d\u2019initier la connexion Microsoft',
          },
          register: {
            title: 'Cr\u00e9er votre compte',
            firstName: 'Pr\u00e9nom',
            lastName: 'Nom de famille',
            email: 'Adresse e-mail',
            password: 'Mot de passe',
            confirmPassword: 'Confirmer le mot de passe',
            terms: 'J\u2019accepte les Conditions d\u2019utilisation et la Politique de confidentialit\u00e9',
            submit: 'Cr\u00e9er un compte',
            alreadyHaveAccount: 'Vous avez d\u00e9j\u00e0 un compte\u2009?',
            signIn: 'Se connecter',
            creating: 'Cr\u00e9ation du compte\u2026',
            heading: 'Cr\u00e9er un compte',
            subheading: 'Rejoignez la plateforme Agion d\u00e8s aujourd\u2019hui',
            goToLogin: 'Aller \u00e0 la page de connexion',
            firstNamePlaceholder: 'Jean',
            lastNamePlaceholder: 'Dupont',
            emailPlaceholder: 'jean@entreprise.fr',
            passwordPlaceholder: 'Cr\u00e9er un mot de passe',
            confirmPasswordPlaceholder: 'Saisissez \u00e0 nouveau le mot de passe',
            acceptTermsLead: 'J\u2019accepte les',
            termsOfService: 'Conditions d\u2019utilisation',
            acceptTermsJoin: 'et la',
            privacyPolicy: 'Politique de confidentialit\u00e9',
            submitButton: 'Cr\u00e9er un compte',
            creatingButton: 'Cr\u00e9ation du compte\u2026',
          },
          organizationSignup: {
            title: 'Cr\u00e9er une organisation',
            description: 'Votre organisation n\u2019existe pas encore. Cr\u00e9ez-en une pour continuer.',
            namePlaceholder: 'Acme SA',
            creating: 'Cr\u00e9ation de l\u2019organisation\u2026',
            submit: 'Cr\u00e9er une organisation',
            backToLogin: 'Retour \u00e0 la connexion',
          },
          mfa: {
            title: 'Authentification \u00e0 deux facteurs',
            description: 'Saisissez le code de v\u00e9rification de votre application d\u2019authentification',
            code: 'Code de v\u00e9rification',
            submit: 'V\u00e9rifier',
            backupCode: 'Utiliser un code de secours',
            havingTrouble: 'Vous rencontrez un probl\u00e8me\u2009?',
            verifying: 'V\u00e9rification\u2026',
            methods: {
              totp: 'Application d\u2019authentification',
              webauthn: 'Cl\u00e9 de s\u00e9curit\u00e9',
              sms: 'SMS',
              email: 'E-mail',
              backup: 'Code de secours',
            },
          },
          consent: {
            title: 'Autoriser {{client}}',
            description: '{{client}} demande l\u2019acc\u00e8s \u00e0 votre compte',
            defaultDescription: 'Cette application demande l\u2019acc\u00e8s \u00e0 votre compte',
            permissions: 'Cette application pourra\u2009:',
            permissionsTitle: 'Cette application pourra\u2009:',
            userInfo: 'Informations partag\u00e9es\u2009:',
            remember: 'M\u00e9moriser mon choix',
            allow: 'Autoriser',
            deny: 'Refuser',
            allowing: 'Autorisation en cours\u2026',
            invalid: 'Demande de consentement invalide',
            invalidDescription: 'La demande de consentement est invalide ou a expir\u00e9.',
            required: 'Obligatoire',
            loggedInAs: 'Connect\u00e9 en tant que {{email}}',
            website: 'Site web',
            privacy: 'Politique de confidentialit\u00e9',
            terms: 'Conditions d\u2019utilisation',
            scopes: {
              openid: 'V\u00e9rifier votre identit\u00e9',
              openidDesc: 'Acc\u00e9der \u00e0 votre identifiant utilisateur unique',
              profile: 'Voir votre profil',
              profileDesc: 'Acc\u00e9der \u00e0 votre nom et \u00e0 vos informations de profil',
              email: 'Voir votre adresse e-mail',
              emailDesc: 'Acc\u00e9der \u00e0 votre adresse e-mail',
              phone: 'Voir votre num\u00e9ro de t\u00e9l\u00e9phone',
              phoneDesc: 'Acc\u00e9der \u00e0 votre num\u00e9ro de t\u00e9l\u00e9phone',
              address: 'Voir votre adresse',
              addressDesc: 'Acc\u00e9der \u00e0 votre adresse postale',
              offlineAccess: 'Rester connect\u00e9',
              offlineAccessDesc: 'Maintenir l\u2019acc\u00e8s lorsque vous n\u2019utilisez pas activement l\u2019application',
              customDesc: 'Acc\u00e8s \u00e0 {{scope}}',
            },
          },
          forgotPassword: {
            title: 'R\u00e9initialiser votre mot de passe',
            description: 'Saisissez votre adresse e-mail et nous vous enverrons un lien pour r\u00e9initialiser votre mot de passe',
            email: 'Adresse e-mail',
            submit: 'Envoyer le lien de r\u00e9initialisation',
            backToLogin: 'Retour \u00e0 la connexion',
            sending: 'Envoi en cours\u2026',
          },
          resetPassword: {
            title: 'D\u00e9finir un nouveau mot de passe',
            description: 'Saisissez votre nouveau mot de passe ci-dessous',
            password: 'Nouveau mot de passe',
            confirmPassword: 'Confirmer le nouveau mot de passe',
            submit: 'R\u00e9initialiser le mot de passe',
            resetting: 'R\u00e9initialisation\u2026',
          },
          emailVerification: {
            title: 'V\u00e9rifiez votre e-mail',
            description: 'Nous avons envoy\u00e9 un lien de v\u00e9rification \u00e0 votre adresse e-mail. Veuillez cliquer sur le lien pour v\u00e9rifier votre compte et continuer.',
            descriptionWithEmail: 'Nous avons envoy\u00e9 un lien de v\u00e9rification \u00e0 {{email}}. Veuillez cliquer sur le lien pour v\u00e9rifier votre compte et continuer.',
            expiryWarning: 'Le lien de v\u00e9rification expirera dans 30 minutes.',
            checkSpam: 'Vous ne voyez pas l\u2019e-mail\u2009? V\u00e9rifiez votre dossier de courrier ind\u00e9sirable.',
            didntReceive: 'Vous n\u2019avez pas re\u00e7u l\u2019e-mail\u2009?',
            resendButton: 'Renvoyer l\u2019e-mail de v\u00e9rification',
            resendSuccess: 'E-mail de v\u00e9rification envoy\u00e9 avec succ\u00e8s\u2009! Le nouveau lien expirera dans 30 minutes.',
            resendError: '\u00c9chec du renvoi de l\u2019e-mail de v\u00e9rification',
            successTitle: 'E-mail v\u00e9rifi\u00e9\u2009!',
            successMessage: 'Votre adresse e-mail a \u00e9t\u00e9 v\u00e9rifi\u00e9e avec succ\u00e8s.',
            verifiedDomain: 'Domaine v\u00e9rifi\u00e9',
            signInToCreateOrg: 'Connectez-vous pour configurer votre organisation.',
            signIn: 'Se connecter',
          },
          device: {
            title: 'Autorisation de l\u2019appareil',
            description: 'Saisissez le code affich\u00e9 sur votre appareil pour vous connecter.',
            codeLabel: 'Code de l\u2019appareil',
            codeHint: 'Saisissez le code affich\u00e9 dans votre terminal',
            continue: 'Continuer',
            verifying: 'V\u00e9rification du code\u2026',
            confirmTitle: 'Autoriser l\u2019appareil',
            confirmDescription: 'Un appareil demande l\u2019acc\u00e8s \u00e0 votre compte.',
            requestingApp: 'Application',
            permissions: 'Autorisations demand\u00e9es',
            approve: 'Approuver',
            deny: 'Refuser',
            securityWarning: 'N\u2019approuvez que si vous \u00eates \u00e0 l\u2019origine de cette demande.',
            successTitle: 'Appareil autoris\u00e9',
            successDescription: 'Votre appareil a \u00e9t\u00e9 autoris\u00e9. Vous pouvez retourner \u00e0 votre terminal.',
            closeWindow: 'Vous pouvez fermer cette fen\u00eatre.',
            deniedTitle: 'Autorisation refus\u00e9e',
            deniedDescription: 'La demande d\u2019autorisation de l\u2019appareil a \u00e9t\u00e9 refus\u00e9e.',
            errorTitle: 'Erreur',
            genericError: 'Un probl\u00e8me est survenu. Veuillez r\u00e9essayer.',
            tryAgain: 'R\u00e9essayer',
            invalidCode: 'Code invalide ou expir\u00e9. Veuillez v\u00e9rifier et r\u00e9essayer.',
            verifyError: '\u00c9chec de la v\u00e9rification du code. Veuillez r\u00e9essayer.',
            approveError: '\u00c9chec de l\u2019autorisation de l\u2019appareil. Veuillez r\u00e9essayer.',
            denyError: '\u00c9chec du refus de la demande. Veuillez r\u00e9essayer.',
            scopes: {
              openid: 'V\u00e9rifier votre identit\u00e9',
              profile: 'Voir votre nom et votre profil',
              email: 'Voir votre adresse e-mail',
              offlineAccess: 'Rester connect\u00e9',
            },
          },
          authorize: {
            title: 'Demande d\u2019autorisation',
            description: '{{app}} demande l\u2019acc\u00e8s \u00e0 votre compte',
            authorizing: 'Autorisation en cours\u2026',
            errorTitle: 'Erreur d\u2019autorisation',
            invalidRequest: 'Demande d\u2019autorisation invalide. Param\u00e8tres obligatoires manquants.',
            invalidRedirectUri: 'URI de redirection invalide pour ce client.',
            processingError: '\u00c9chec du traitement de la demande d\u2019autorisation.',
            authorizationFailed: '\u00c9chec de l\u2019autorisation',
            invalidServerResponse: 'R\u00e9ponse invalide du serveur d\u2019autorisation',
            redirectMismatch: 'URL de redirection invalide du serveur \u2014 ne correspond pas au redirect_uri enregistr\u00e9',
            permissionsTitle: 'Cette application pourra\u2009:',
            secureTitle: 'Autorisation s\u00e9curis\u00e9e',
            secureDescription: 'Cette autorisation utilise le standard OAuth 2.0 avec PKCE pour une s\u00e9curit\u00e9 renforc\u00e9e.',
            approve: 'Autoriser',
            deny: 'Refuser',
            defaultUser: 'Utilisateur',
            footerConsent: 'En autorisant, vous permettez \u00e0 {{app}} d\u2019acc\u00e9der \u00e0 votre compte Agion en votre nom.',
            footerRevoke: 'Vous pouvez r\u00e9voquer cet acc\u00e8s \u00e0 tout moment depuis les param\u00e8tres de votre compte.',
          },
          layout: {
            privacyPolicy: 'Politique de confidentialit\u00e9',
            termsOfService: 'Conditions d\u2019utilisation',
            support: 'Assistance',
            skipToContent: 'Aller au contenu principal',
            allRightsReserved: 'Tous droits r\u00e9serv\u00e9s.',
          },
          backToLogin: 'Retour \u00e0 la connexion',
          waitlist: {
            checkEmailTitle: 'Vérifiez votre e-mail',
            checkEmailExpiry: 'Le lien expire dans 24 heures.',
            signedInAs: 'Connecté en tant que',
            loading: 'Chargement',
            emailInvalidWork: 'Saisissez une adresse e-mail professionnelle valide.',
            requestAccessTitle: 'Demander l’accès',
            requestAccessSubtitle: 'Agion intègre les organisations par vagues. Dites-nous où vous travaillez et nous vous contacterons.',
            requestAccessButton: 'Demander l’accès',
            orRequestWith: 'ou demander l’accès avec',
            oneMoreStepTitle: 'Encore une étape',
            oneMoreStepSubtitle: 'Ajoutez le nom de votre organisation pour terminer, puis continuez avec le fournisseur que vous avez utilisé.',
            continueWithGoogle: 'Continuer avec Google',
            continueWithMicrosoft: 'Continuer avec Microsoft',
            checkEmailMessageRich: 'Nous avons envoyé un lien et un code de vérification à <b>{{email}}</b>. Cliquez sur le lien ou saisissez le code ci-dessous.',
            codeLabel: 'Code de vérification',
            codePlaceholder: '123456',
            codeHint: 'Saisissez le code à 6 chiffres reçu par e-mail. Il expire dans 15 minutes.',
            codeSubmit: 'Continuer',
            codeInvalid: 'Ce code n’est pas valide ou a expiré. Vérifiez l’e-mail ou demandez-en un nouveau.',
            didntGetEmail: 'Vous n’avez pas reçu l’e-mail ?',
            onListTitle: 'Vous êtes sur la liste',
            onListMessage: 'Merci de votre intérêt pour Agion. Nous vous enverrons un e-mail dès que l’accès sera ouvert pour votre organisation.',
            onListInbox: 'Consultez votre boîte de réception pour les mises à jour.',
            setPasswordTitle: 'Définissez votre mot de passe',
            setPasswordSubtitle: 'Confirmez vos informations et choisissez un mot de passe pour finaliser votre inscription.',
            workEmailLabel: 'E-mail professionnel',
            verifiedMarker: 'Vérifié',
            passwordWeak: 'Mot de passe faible',
            passwordGood: 'Bon mot de passe',
            passwordStrong: 'Mot de passe fort',
            showPassword: 'Afficher',
            hidePassword: 'Masquer',
            passwordsDontMatch: 'Les mots de passe ne correspondent pas.',
            passwordsMatch: 'Les mots de passe correspondent',
            confirmJoinButton: 'Confirmer et rejoindre',
            pendingApprovalTitle: 'Demande en attente d’approbation',
            pendingApprovalBodyRich: 'Votre organisation est déjà sur Agion. Nous avons envoyé votre demande pour rejoindre <b>{{organization}}</b> à ses administrateurs.',
            pendingApprovalBodyGeneric: 'Votre organisation est déjà sur Agion. Nous avons envoyé votre demande d’adhésion à ses administrateurs.',
            whatHappensNext: 'Ce qui se passe ensuite',
            pendingStep1: 'Un administrateur examine votre demande d’adhésion',
            pendingStep2: 'Vous êtes approuvé et ajouté à l’espace de travail',
            pendingStep3: 'Vous recevrez un e-mail — puis vous pourrez vous connecter',
            wrongOrg: 'Mauvaise organisation ? Contactez',
            backToSignIn: 'Retour à la connexion',
            title: 'Rejoindre la liste d\u2019attente Agion',
            subtitle: 'Ex\u00e9cutez des essaims d\u2019agents IA en toute s\u00e9curit\u00e9. Cr\u00e9ez un compte pour rejoindre notre liste d\u2019attente d\u00e8s aujourd\u2019hui.',
            emailLabel: 'E-mail professionnel',
            emailPlaceholder: 'vous@entreprise.com',
            orgLabel: 'Nom de l\u2019organisation',
            orgPlaceholder: 'Votre entreprise ou organisation',
            joinButton: 'Rejoindre la liste d\u2019attente',
            submitting: 'Envoi en cours\u2026',
            orSignUpWith: 'ou s\u2019inscrire avec',
            googleSignUp: 'S\u2019inscrire avec Google',
            microsoftSignUp: 'S\u2019inscrire avec Microsoft',
            hasAccount: 'Vous avez d\u00e9j\u00e0 un compte\u2009?',
            signIn: 'Se connecter',
            blockedDomain: 'Veuillez utiliser votre adresse e-mail professionnelle',
            emailRequired: 'L\u2019adresse e-mail est obligatoire',
            emailInvalid: 'Veuillez saisir une adresse e-mail valide',
            orgRequired: 'Le nom de l\u2019organisation est obligatoire',
            genericError: 'Une erreur est survenue',
            successTitle: 'Merci\u2009!',
            successMessage: 'Nous avons bien re\u00e7u votre demande d\u2019int\u00e9r\u00eat pour Agion. Nous vous contacterons lorsque l\u2019acc\u00e8s sera disponible pour votre organisation.',
            successInbox: 'Consultez votre bo\u00eete de r\u00e9ception pour les mises \u00e0 jour.',
            copyright: 'Agion. Tous droits r\u00e9serv\u00e9s.',
            confirmTitle: 'Confirmez vos informations',
            confirmSubtitle: 'Veuillez v\u00e9rifier vos informations avant de rejoindre la liste d\u2019attente.',
            confirmButton: 'Confirmer et rejoindre la liste d\u2019attente',
            passwordLabel: 'Mot de passe',
            passwordPlaceholder: 'Cr\u00e9er un mot de passe',
            confirmPasswordLabel: 'Confirmer le mot de passe',
            confirmPasswordPlaceholder: 'Ressaisissez le mot de passe',
            passwordRequired: 'Le mot de passe est obligatoire',
            passwordMismatch: 'Les mots de passe ne correspondent pas',
            linkExpiredTitle: 'Lien expir\u00e9',
            backToWaitlist: 'Retour à la demande d’accès',
            invalidRequest: 'Demande invalide. Veuillez r\u00e9essayer.',
            tokenExpired: 'Jeton invalide ou expir\u00e9',
            didntReceiveEmail: 'Vous n\u2019avez pas re\u00e7u l\u2019e-mail\u2009?',
            resendButton: 'Renvoyer l\u2019e-mail de v\u00e9rification',
            resendSuccess: 'E-mail de v\u00e9rification envoy\u00e9\u2009! V\u00e9rifiez votre bo\u00eete de r\u00e9ception.',
            resendCooldown: 'Renvoyer dans {{seconds}}s',
            pendingTitle: 'Votre compte est en attente d\u2019approbation',
            pendingMessage: 'Nous vous informerons par e-mail lorsque votre organisation sera prête. Vous pourrez vous connecter avec le même compte que celui utilisé pour rejoindre la liste d’attente.',
            pendingContact: 'Des questions ? Contactez-nous à',
            supportEmail: 'support@agion.ai',
          },
          socialLogin: {
            google: 'Continuer avec Google',
            github: 'Continuer avec GitHub',
            microsoft: 'Continuer avec Microsoft',
            apple: 'Continuer avec Apple',
          },
          social: {
            google: 'Google',
            microsoft: 'Microsoft',
          },
          invite: {
            title: 'Rejoindre {{organization}}',
            titleDefault: 'Invitation \u00e0 rejoindre une organisation',
            subtitle: '{{inviter}} vous a invit\u00e9 \u00e0 rejoindre en tant que {{role}}',
            subtitleDefault: 'Vous avez \u00e9t\u00e9 invit\u00e9 \u00e0 rejoindre en tant que {{role}}',
            processing: 'Traitement de votre invitation\u2026',
            redirectingToLogin: 'Redirection vers la connexion\u2026',
            validating: 'Validation de votre invitation\u2026',
            successAccepted: 'Invitation accept\u00e9e\u2009!',
            successRegistered: 'Inscription r\u00e9ussie\u2009!',
            successJoined: 'Vous avez rejoint {{organization}}.',
            redirecting: 'Redirection vers la connexion\u2026',
            errorTitle: 'Erreur d\u2019invitation',
            errorInvalidToken: 'Ce lien d\u2019invitation est invalide. Veuillez v\u00e9rifier le lien ou demander une nouvelle invitation.',
            errorExpired: 'Cette invitation a expir\u00e9. Veuillez demander une nouvelle invitation.',
            errorAlreadyAccepted: 'Cette invitation a d\u00e9j\u00e0 \u00e9t\u00e9 accept\u00e9e.',
            errorNetwork: '\u00c9chec de la validation de l\u2019invitation. Veuillez v\u00e9rifier votre connexion et r\u00e9essayer.',
            errorGeneric: 'Cette invitation est invalide ou a expir\u00e9. Veuillez demander une nouvelle invitation.',
            errorNoToken: 'Aucun jeton d\u2019invitation fourni. Veuillez utiliser le lien de votre e-mail d\u2019invitation.',
            needHelp: 'Besoin d\u2019aide\u2009?',
            contactSupport: 'Contacter l\u2019assistance',
            wrongAccount: 'Mauvais compte\u2009?',
            switchAccount: 'Se connecter avec un autre compte',
            hasAccount: 'Vous avez d\u00e9j\u00e0 un compte\u2009?',
            signInInstead: 'Se connecter',
            email: 'E-mail',
            verificationCode: 'Code de v\u00e9rification',
            verificationCodePlaceholder: 'Saisissez le code \u00e0 6 chiffres',
            verificationCodeHint: 'V\u00e9rifiez votre e-mail pour le code de v\u00e9rification \u00e0 6 chiffres',
            firstName: 'Pr\u00e9nom',
            firstNamePlaceholder: 'Pr\u00e9nom',
            lastName: 'Nom de famille',
            lastNamePlaceholder: 'Nom de famille',
            password: 'Mot de passe',
            passwordPlaceholder: 'Cr\u00e9er un mot de passe',
            confirmPassword: 'Confirmer le mot de passe',
            confirmPasswordPlaceholder: 'Confirmez votre mot de passe',
            acceptButton: 'Accepter l\u2019invitation',
            accepting: 'Acceptation\u2026',
            createButton: 'Cr\u00e9er un compte et rejoindre',
            creating: 'Cr\u00e9ation du compte\u2026',
            orSignInWith: 'Ou se connecter avec',
            orContinueWith: 'Ou continuer avec',
            termsNotice: 'En cr\u00e9ant un compte, vous acceptez nos',
            termsOfService: 'Conditions d\u2019utilisation',
            privacyPolicy: 'Politique de confidentialit\u00e9',
            goToLogin: 'Aller \u00e0 la connexion',
            validationCodeRequired: 'Veuillez saisir le code de v\u00e9rification \u00e0 6 chiffres de votre e-mail d\u2019invitation.',
            validationNameRequired: 'Le pr\u00e9nom et le nom de famille sont obligatoires.',
            validationPasswordMatch: 'Les mots de passe ne correspondent pas.',
            acceptFailed: '\u00c9chec de l\u2019acceptation de l\u2019invitation. Veuillez v\u00e9rifier votre code de v\u00e9rification.',
            registrationFailed: '\u00c9chec de l\u2019inscription. Veuillez v\u00e9rifier votre code de v\u00e9rification et r\u00e9essayer.',
            didntReceiveCode: 'Vous n\u2019avez pas re\u00e7u le code\u2009?',
            resendButton: 'Renvoyer le code',
            resendSuccess: 'Un nouveau code de v\u00e9rification a \u00e9t\u00e9 envoy\u00e9 \u00e0 votre adresse e-mail.',
            resendError: '\u00c9chec du renvoi du code. Veuillez r\u00e9essayer.',
            resendCooldown: 'Renvoyer dans {{seconds}}s',
          },
          passwordReset: frPasswordReset,
          newPassword: {
            hint: 'Au moins {{min}} caractères.',
            hintWithNumberOrSpecial: 'Au moins {{min}} caractères, dont un chiffre ou un caractère spécial.',
          },
          logout: {
            signingOut: 'D\u00e9connexion en cours\u2026',
            error: '\u00c9chec de la d\u00e9connexion',
            redirecting: 'Redirection vers la connexion\u2026',
          },
        },
        common: {
          loading: 'Chargement\u2026',
          error: 'Erreur',
          success: 'Succ\u00e8s',
          retry: 'R\u00e9essayer',
          cancel: 'Annuler',
          continue: 'Continuer',
          back: 'Retour',
          next: 'Suivant',
          finish: 'Terminer',
          close: 'Fermer',
          save: 'Enregistrer',
          email: 'E-mail',
          password: 'Mot de passe',
          name: 'Nom',
          required: 'Obligatoire',
          optional: 'Facultatif',
        },
        errors: {
          invalidCredentials: 'Adresse e-mail ou mot de passe invalide',
          accountLocked: 'Compte verrouill\u00e9. Veuillez r\u00e9essayer plus tard.',
          networkError: 'Erreur r\u00e9seau. Veuillez v\u00e9rifier votre connexion.',
          sessionExpired: 'Votre session a expir\u00e9. Veuillez vous reconnecter.',
          invalidToken: 'Jeton invalide ou expir\u00e9',
          emailRequired: 'L\u2019adresse e-mail est obligatoire',
          passwordRequired: 'Le mot de passe est obligatoire',
          passwordTooShort: 'Le mot de passe doit contenir au moins 8 caract\u00e8res',
          passwordsDoNotMatch: 'Les mots de passe ne correspondent pas',
          invalidEmail: 'Veuillez saisir une adresse e-mail valide',
          termsRequired: 'Vous devez accepter les conditions g\u00e9n\u00e9rales',
          mfaRequired: 'L\u2019authentification \u00e0 deux facteurs est requise',
          invalidMfaCode: 'Code de v\u00e9rification invalide',
          rateLimited: 'Trop de tentatives. Veuillez r\u00e9essayer plus tard.',
          serverError: 'Erreur du serveur. Veuillez r\u00e9essayer plus tard.',
          unknownError: 'Une erreur inattendue est survenue',
        },
      },
      fi: {
        auth: {
          signInRecovery: fiSignInRecovery,
          authErrors: fiAuthErrors,
          login: {
            copyright: '© {{year}} Agion. Kaikki oikeudet pidätetään.',
            headline: 'Agentit, turvallisesti hallinnassasi.',
            subline: 'Rakenna ja hallitse luotettavia agenttiparvia, jotka muuttavat datasi älyksi — valitsemassasi pilvessä, malleissa ja sovelluksissa. Suvereniteetti, toimitettuna.',
            sublineShort: 'Rakenna ja hallitse luotettavia agenttiparvia — omassa pilvessäsi, malleissasi ja sovelluksissasi.',
            formHeading: 'Tervetuloa takaisin',
            formSubheading: 'Kirjaudu Agion-työtilaasi.',
            showPassword: 'Näytä',
            hidePassword: 'Piilota',
            newToAgion: 'Uusi Agionissa?',
            joinWaitlist: 'Liity jonotuslistalle',
            languageSwitch: 'Kieli',
            title: 'Kirjautukaa tilillenne',
            email: 'S\u00e4hk\u00f6postiosoite',
            emailLabel: 'Sähköposti',
            emailPlaceholder: 'te@esimerkki.fi',
            password: 'Salasana',
            passwordLabel: 'Salasana',
            remember: 'Muista minut',
            submit: 'Kirjaudu',
            forgotPassword: 'Unohditko salasanan?',
            createAccount: 'Luokaa tilinne',
            noAccount: 'Uusi Agionin k\u00e4ytt\u00e4j\u00e4?',
            orContinueWith: 'tai jatka',
            signingIn: 'Kirjaudutaan\u2026',
            googleSignIn: 'Kirjaudu Google-tilill\u00e4',
            microsoftSignIn: 'Kirjaudu Microsoft-tilill\u00e4',
            allRightsReserved: 'Kaikki oikeudet pid\u00e4tet\u00e4\u00e4n.',
            missingRedirect: 'Kirjautumisvastaus ei sis\u00e4ll\u00e4 uudelleenohjaus-URL:a',
            genericError: 'Kirjautumisen aikana tapahtui virhe',
            googleError: 'Google-kirjautumisen aloittaminen ep\u00e4onnistui',
            microsoftError: 'Microsoft-kirjautumisen aloittaminen ep\u00e4onnistui',
          },
          register: {
            title: 'Luokaa tilinne',
            firstName: 'Etunimi',
            lastName: 'Sukunimi',
            email: 'S\u00e4hk\u00f6postiosoite',
            password: 'Salasana',
            confirmPassword: 'Vahvistakaa salasana',
            terms: 'Hyv\u00e4ksyn k\u00e4ytt\u00f6ehdot ja tietosuojak\u00e4yt\u00e4nn\u00f6n',
            submit: 'Luo tili',
            alreadyHaveAccount: 'Onko teill\u00e4 jo tili?',
            signIn: 'Kirjaudu sis\u00e4\u00e4n',
            creating: 'Luodaan tili\u00e4\u2026',
            heading: 'Luo tili',
            subheading: 'Liittyk\u00e4\u00e4 Agion-alustalle jo t\u00e4n\u00e4\u00e4n',
            goToLogin: 'Siirry kirjautumissivulle',
            firstNamePlaceholder: 'Matti',
            lastNamePlaceholder: 'Meik\u00e4l\u00e4inen',
            emailPlaceholder: 'matti@yritys.fi',
            passwordPlaceholder: 'Luokaa salasana',
            confirmPasswordPlaceholder: 'Kirjoittakaa salasana uudelleen',
            acceptTermsLead: 'Hyv\u00e4ksyn',
            termsOfService: 'k\u00e4ytt\u00f6ehdot',
            acceptTermsJoin: 'ja',
            privacyPolicy: 'tietosuojak\u00e4yt\u00e4nn\u00f6n',
            submitButton: 'Luo tili',
            creatingButton: 'Luodaan tili\u00e4\u2026',
          },
          organizationSignup: {
            title: 'Luo organisaatio',
            description: 'Organisaatiotanne ei ole viel\u00e4 olemassa. Luokaa se jatkaaksenne.',
            namePlaceholder: 'Acme Oy',
            creating: 'Luodaan organisaatiota\u2026',
            submit: 'Luo organisaatio',
            backToLogin: 'Takaisin kirjautumiseen',
          },
          mfa: {
            title: 'Kaksivaiheinen tunnistautuminen',
            description: 'Sy\u00f6tt\u00e4k\u00e4\u00e4 vahvistuskoodi todennussovelluksestanne',
            code: 'Vahvistuskoodi',
            submit: 'Vahvista',
            backupCode: 'K\u00e4yt\u00e4 varakoodia',
            havingTrouble: 'Ongelmia?',
            verifying: 'Vahvistetaan\u2026',
            methods: {
              totp: 'Todennussovellus',
              webauthn: 'Turva-avain',
              sms: 'Tekstiviesti',
              email: 'S\u00e4hk\u00f6posti',
              backup: 'Varakoodi',
            },
          },
          consent: {
            title: 'Valtuuta {{client}}',
            description: '{{client}} pyyt\u00e4\u00e4 p\u00e4\u00e4sy\u00e4 tiliinne',
            defaultDescription: 'T\u00e4m\u00e4 sovellus pyyt\u00e4\u00e4 p\u00e4\u00e4sy\u00e4 tiliinne',
            permissions: 'T\u00e4m\u00e4 sovellus voi:',
            permissionsTitle: 'T\u00e4m\u00e4 sovellus voi:',
            userInfo: 'Jaettavat tiedot:',
            remember: 'Muista valintani',
            allow: 'Salli',
            deny: 'Est\u00e4',
            allowing: 'Valtuutetaan\u2026',
            invalid: 'Virheellinen suostumuspyynt\u00f6',
            invalidDescription: 'Suostumuspyynt\u00f6 on virheellinen tai vanhentunut.',
            required: 'Pakollinen',
            loggedInAs: 'Kirjautunut k\u00e4ytt\u00e4j\u00e4n\u00e4 {{email}}',
            website: 'Verkkosivusto',
            privacy: 'Tietosuojak\u00e4yt\u00e4nt\u00f6',
            terms: 'K\u00e4ytt\u00f6ehdot',
            scopes: {
              openid: 'Vahvistaa henkil\u00f6llisyytenne',
              openidDesc: 'P\u00e4\u00e4sy yksil\u00f6lliseen k\u00e4ytt\u00e4j\u00e4tunnisteeseenne',
              profile: 'N\u00e4hd\u00e4 profiilinne',
              profileDesc: 'P\u00e4\u00e4sy nimeenne ja profiilitietoihinne',
              email: 'N\u00e4hd\u00e4 s\u00e4hk\u00f6postiosoitteenne',
              emailDesc: 'P\u00e4\u00e4sy s\u00e4hk\u00f6postiosoitteeseenne',
              phone: 'N\u00e4hd\u00e4 puhelinnumeronne',
              phoneDesc: 'P\u00e4\u00e4sy puhelinnumeroonne',
              address: 'N\u00e4hd\u00e4 osoitteenne',
              addressDesc: 'P\u00e4\u00e4sy postiosoitteeseenne',
              offlineAccess: 'Pysy\u00e4 kirjautuneena',
              offlineAccessDesc: 'Yll\u00e4pit\u00e4\u00e4 p\u00e4\u00e4sy\u00e4, kun ette aktiivisesti k\u00e4yt\u00e4 sovellusta',
              customDesc: 'P\u00e4\u00e4sy kohteeseen {{scope}}',
            },
          },
          forgotPassword: {
            title: 'Palauttakaa salasananne',
            description: 'Sy\u00f6tt\u00e4k\u00e4\u00e4 s\u00e4hk\u00f6postiosoitteenne, niin l\u00e4het\u00e4mme teille salasanan palautuslinkin',
            email: 'S\u00e4hk\u00f6postiosoite',
            submit: 'L\u00e4het\u00e4 palautuslinkki',
            backToLogin: 'Takaisin kirjautumiseen',
            sending: 'L\u00e4hetet\u00e4\u00e4n\u2026',
          },
          resetPassword: {
            title: 'Asettakaa uusi salasana',
            description: 'Sy\u00f6tt\u00e4k\u00e4\u00e4 uusi salasananne alle',
            password: 'Uusi salasana',
            confirmPassword: 'Vahvistakaa uusi salasana',
            submit: 'Palauta salasana',
            resetting: 'Palautetaan\u2026',
          },
          emailVerification: {
            title: 'Tarkistakaa s\u00e4hk\u00f6postinne',
            description: 'Olemme l\u00e4hett\u00e4neet vahvistuslinkin s\u00e4hk\u00f6postiosoitteeseenne. Napsauttakaa linkki\u00e4 vahvistaaksenne tilinn\u00e4 ja jatkaaksenne.',
            descriptionWithEmail: 'Olemme l\u00e4hett\u00e4neet vahvistuslinkin osoitteeseen {{email}}. Napsauttakaa linkki\u00e4 vahvistaaksenne tilinne ja jatkaaksenne.',
            expiryWarning: 'Vahvistuslinkki vanhenee 30 minuutissa.',
            checkSpam: 'Etk\u00f6 n\u00e4e viesti\u00e4? Tarkistakaa roskaposti- tai roskapostikansio.',
            didntReceive: 'Etk\u00f6 saanut viesti\u00e4?',
            resendButton: 'L\u00e4het\u00e4 vahvistusviesti uudelleen',
            resendSuccess: 'Vahvistusviesti l\u00e4hetetty! Uusi linkki vanhenee 30 minuutissa.',
            resendError: 'Vahvistusviestin uudelleenl\u00e4hetys ep\u00e4onnistui',
            successTitle: 'S\u00e4hk\u00f6posti vahvistettu!',
            successMessage: 'S\u00e4hk\u00f6postiosoitteenne on vahvistettu onnistuneesti.',
            verifiedDomain: 'Vahvistettu verkkotunnus',
            signInToCreateOrg: 'Kirjautukaa sis\u00e4\u00e4n m\u00e4\u00e4ritt\u00e4\u00e4ksenne organisaationne.',
            signIn: 'Kirjaudu sis\u00e4\u00e4n',
          },
          device: {
            title: 'Laitteen valtuutus',
            description: 'Sy\u00f6tt\u00e4k\u00e4\u00e4 laitteellanne n\u00e4kyv\u00e4 koodi kirjautuaksenne sis\u00e4\u00e4n.',
            codeLabel: 'Laitekoodi',
            codeHint: 'Sy\u00f6tt\u00e4k\u00e4\u00e4 terminaalissa n\u00e4kyv\u00e4 koodi',
            continue: 'Jatka',
            verifying: 'Tarkistetaan koodia\u2026',
            confirmTitle: 'Valtuuta laite',
            confirmDescription: 'Laite pyyt\u00e4\u00e4 p\u00e4\u00e4sy\u00e4 tiliinne.',
            requestingApp: 'Sovellus',
            permissions: 'Pyydetyt k\u00e4ytt\u00f6oikeudet',
            approve: 'Hyv\u00e4ksy',
            deny: 'Hylk\u00e4\u00e4',
            securityWarning: 'Hyv\u00e4ksyk\u00e4\u00e4 vain, jos aloititte t\u00e4m\u00e4n pyynn\u00f6n.',
            successTitle: 'Laite valtuutettu',
            successDescription: 'Laitteenne on valtuutettu. Voitte palata terminaaliinne.',
            closeWindow: 'Voitte sulkea t\u00e4m\u00e4n ikkunan.',
            deniedTitle: 'Valtuutus ev\u00e4tty',
            deniedDescription: 'Laitteen valtuutuspyynt\u00f6 hyl\u00e4ttiin.',
            errorTitle: 'Virhe',
            genericError: 'Jokin meni vikaan. Yritt\u00e4k\u00e4\u00e4 uudelleen.',
            tryAgain: 'Yrit\u00e4 uudelleen',
            invalidCode: 'Virheellinen tai vanhentunut koodi. Tarkistakaa ja yritt\u00e4k\u00e4\u00e4 uudelleen.',
            verifyError: 'Koodin tarkistus ep\u00e4onnistui. Yritt\u00e4k\u00e4\u00e4 uudelleen.',
            approveError: 'Laitteen valtuutus ep\u00e4onnistui. Yritt\u00e4k\u00e4\u00e4 uudelleen.',
            denyError: 'Pyynn\u00f6n hylk\u00e4\u00e4minen ep\u00e4onnistui. Yritt\u00e4k\u00e4\u00e4 uudelleen.',
            scopes: {
              openid: 'Vahvistaa henkil\u00f6llisyytenne',
              profile: 'N\u00e4hd\u00e4 nimenne ja profiilinne',
              email: 'N\u00e4hd\u00e4 s\u00e4hk\u00f6postiosoitteenne',
              offlineAccess: 'Pysy\u00e4 kirjautuneena',
            },
          },
          authorize: {
            title: 'Valtuutuspyynt\u00f6',
            description: '{{app}} pyyt\u00e4\u00e4 p\u00e4\u00e4sy\u00e4 tiliinne',
            authorizing: 'Valtuutetaan\u2026',
            errorTitle: 'Valtuutusvirhe',
            invalidRequest: 'Virheellinen valtuutuspyynt\u00f6. Pakolliset parametrit puuttuvat.',
            invalidRedirectUri: 'Virheellinen uudelleenohjaus-URI t\u00e4lle asiakassovellukselle.',
            processingError: 'Valtuutuspyynn\u00f6n k\u00e4sittely ep\u00e4onnistui.',
            authorizationFailed: 'Valtuutus ep\u00e4onnistui',
            invalidServerResponse: 'Virheellinen vastaus valtuutuspalvelimelta',
            redirectMismatch: 'Virheellinen uudelleenohjaus-URL palvelimelta \u2014 ei vastaa rekister\u00f6ity\u00e4 redirect_uri-osoitetta',
            permissionsTitle: 'T\u00e4m\u00e4 sovellus voi:',
            secureTitle: 'Turvallinen valtuutus',
            secureDescription: 'T\u00e4m\u00e4 valtuutus k\u00e4ytt\u00e4\u00e4 alan standardia OAuth 2.0 ja PKCE:t\u00e4 paremman tietoturvan takaamiseksi.',
            approve: 'Valtuuta',
            deny: 'Est\u00e4',
            defaultUser: 'K\u00e4ytt\u00e4j\u00e4',
            footerConsent: 'Valtuuttamalla annatte sovellukselle {{app}} luvan k\u00e4ytt\u00e4\u00e4 Agion-tili\u00e4nne puolestanne.',
            footerRevoke: 'Voitte peruuttaa t\u00e4m\u00e4n p\u00e4\u00e4syn milloin tahansa tiliasetuksissanne.',
          },
          layout: {
            privacyPolicy: 'Tietosuojak\u00e4yt\u00e4nt\u00f6',
            termsOfService: 'K\u00e4ytt\u00f6ehdot',
            support: 'Tuki',
            skipToContent: 'Siirry p\u00e4\u00e4sis\u00e4lt\u00f6\u00f6n',
            allRightsReserved: 'Kaikki oikeudet pid\u00e4tet\u00e4\u00e4n.',
          },
          backToLogin: 'Takaisin kirjautumiseen',
          waitlist: {
            checkEmailTitle: 'Tarkista sähköpostisi',
            checkEmailExpiry: 'Linkki vanhenee 24 tunnin kuluttua.',
            signedInAs: 'Kirjautuneena',
            loading: 'Ladataan',
            emailInvalidWork: 'Anna kelvollinen työsähköposti.',
            requestAccessTitle: 'Pyydä käyttöoikeutta',
            requestAccessSubtitle: 'Agion ottaa organisaatioita käyttöön vaiheittain. Kerro missä työskentelet, niin olemme yhteydessä.',
            requestAccessButton: 'Pyydä käyttöoikeutta',
            orRequestWith: 'tai pyydä käyttöoikeutta palvelulla',
            oneMoreStepTitle: 'Vielä yksi vaihe',
            oneMoreStepSubtitle: 'Lisää organisaatiosi nimi viimeistelläksesi ja jatka käyttämälläsi palveluntarjoajalla.',
            continueWithGoogle: 'Jatka Googlella',
            continueWithMicrosoft: 'Jatka Microsoftilla',
            checkEmailMessageRich: 'Lähetimme vahvistuslinkin ja -koodin osoitteeseen <b>{{email}}</b>. Napsauta linkkiä tai syötä koodi alle.',
            codeLabel: 'Vahvistuskoodi',
            codePlaceholder: '123456',
            codeHint: 'Syötä sähköpostin 6-numeroinen koodi. Se vanhenee 15 minuutissa.',
            codeSubmit: 'Jatka',
            codeInvalid: 'Koodi ei kelpaa tai se on vanhentunut. Tarkista sähköposti tai pyydä uusi.',
            didntGetEmail: 'Etkö saanut sähköpostia?',
            onListTitle: 'Olet listalla',
            onListMessage: 'Kiitos kiinnostuksestasi Agionia kohtaan. Lähetämme sähköpostia heti, kun käyttöoikeus avautuu organisaatiollesi.',
            onListInbox: 'Tarkista päivitykset sähköpostistasi.',
            setPasswordTitle: 'Aseta salasanasi',
            setPasswordSubtitle: 'Vahvista tietosi ja valitse salasana viimeistelläksesi liittymisen.',
            workEmailLabel: 'Työsähköposti',
            verifiedMarker: 'Vahvistettu',
            passwordWeak: 'Heikko salasana',
            passwordGood: 'Hyvä salasana',
            passwordStrong: 'Vahva salasana',
            showPassword: 'Näytä',
            hidePassword: 'Piilota',
            passwordsDontMatch: 'Salasanat eivät täsmää.',
            passwordsMatch: 'Salasanat täsmäävät',
            confirmJoinButton: 'Vahvista ja liity',
            pendingApprovalTitle: 'Pyyntö odottaa hyväksyntää',
            pendingApprovalBodyRich: 'Organisaatiosi on jo Agionissa. Lähetimme liittymispyyntösi organisaatioon <b>{{organization}}</b> sen järjestelmänvalvojille.',
            pendingApprovalBodyGeneric: 'Organisaatiosi on jo Agionissa. Lähetimme liittymispyyntösi sen järjestelmänvalvojille.',
            whatHappensNext: 'Mitä tapahtuu seuraavaksi',
            pendingStep1: 'Järjestelmänvalvoja tarkistaa liittymispyyntösi',
            pendingStep2: 'Sinut hyväksytään ja lisätään työtilaan',
            pendingStep3: 'Saat sähköpostin — sitten voit kirjautua sisään',
            wrongOrg: 'Väärä organisaatio? Ota yhteyttä',
            backToSignIn: 'Takaisin kirjautumiseen',
            title: 'Liittykaa Agionin jonotuslistalle',
            subtitle: 'Ajatelkaa teko\u00e4lyagentteja turvallisesti. Luokaa tili liitty\u00e4ksenne jonotuslistallemme.',
            emailLabel: 'Ty\u00f6s\u00e4hk\u00f6posti',
            emailPlaceholder: 'te@yritys.fi',
            orgLabel: 'Organisaation nimi',
            orgPlaceholder: 'Yrityksenne tai organisaationne',
            joinButton: 'Liity jonotuslistalle',
            submitting: 'L\u00e4hetet\u00e4\u00e4n\u2026',
            orSignUpWith: 'tai rekister\u00f6idy palvelulla',
            googleSignUp: 'Rekister\u00f6idy Google-tilill\u00e4',
            microsoftSignUp: 'Rekister\u00f6idy Microsoft-tilill\u00e4',
            hasAccount: 'Onko teill\u00e4 jo tili?',
            signIn: 'Kirjaudu sis\u00e4\u00e4n',
            blockedDomain: 'K\u00e4ytt\u00e4k\u00e4\u00e4 ty\u00f6s\u00e4hk\u00f6postiosoitettanne',
            emailRequired: 'S\u00e4hk\u00f6postiosoite on pakollinen',
            emailInvalid: 'Sy\u00f6tt\u00e4k\u00e4\u00e4 kelvollinen s\u00e4hk\u00f6postiosoite',
            orgRequired: 'Organisaation nimi on pakollinen',
            genericError: 'Tapahtui virhe',
            successTitle: 'Kiitos!',
            successMessage: 'Olemme vastaanottaneet kiinnostuksenne Agionia kohtaan. Otamme yhteytt\u00e4, kun p\u00e4\u00e4sy on saatavilla organisaatiollenne.',
            successInbox: 'Tarkistakaa saapuneet-kansionne p\u00e4ivitysten varalta.',
            copyright: 'Agion. Kaikki oikeudet pid\u00e4tet\u00e4\u00e4n.',
            confirmTitle: 'Vahvistakaa tietonne',
            confirmSubtitle: 'Tarkistakaa tietonne ennen jonotuslistalle liittymist\u00e4.',
            confirmButton: 'Vahvista ja liity jonotuslistalle',
            passwordLabel: 'Salasana',
            passwordPlaceholder: 'Luokaa salasana',
            confirmPasswordLabel: 'Vahvista salasana',
            confirmPasswordPlaceholder: 'Anna salasana uudelleen',
            passwordRequired: 'Salasana on pakollinen',
            passwordMismatch: 'Salasanat eiv\u00e4t t\u00e4sm\u00e4\u00e4',
            linkExpiredTitle: 'Linkki vanhentunut',
            backToWaitlist: 'Takaisin käyttöoikeuspyyntöön',
            invalidRequest: 'Virheellinen pyyntö. Yritä uudelleen.',
            tokenExpired: 'Virheellinen tai vanhentunut tunnus',
            didntReceiveEmail: 'Etk\u00f6 saanut viesti\u00e4?',
            resendButton: 'L\u00e4het\u00e4 vahvistusviesti uudelleen',
            resendSuccess: 'Vahvistusviesti l\u00e4hetetty! Tarkistakaa saapuneet-kansionne.',
            resendCooldown: 'L\u00e4het\u00e4 uudelleen {{seconds}} s kuluttua',
            pendingTitle: 'Tilisi odottaa hyväksyntää',
            pendingMessage: 'Ilmoitamme sinulle sähköpostitse, kun organisaatiosi on valmis. Voit kirjautua sisään samalla tilillä, jolla liityit jonotuslistalle.',
            pendingContact: 'Kysyttävää? Ota yhteyttä',
            supportEmail: 'support@agion.ai',
          },
          socialLogin: {
            google: 'Jatka Google-tilill\u00e4',
            github: 'Jatka GitHub-tilill\u00e4',
            microsoft: 'Jatka Microsoft-tilill\u00e4',
            apple: 'Jatka Apple-tilill\u00e4',
          },
          social: {
            google: 'Google',
            microsoft: 'Microsoft',
          },
          invite: {
            title: 'Liittykaa organisaatioon {{organization}}',
            titleDefault: 'Kutsu organisaatioon',
            subtitle: '{{inviter}} on kutsunut teid\u00e4t liittym\u00e4\u00e4n roolissa {{role}}',
            subtitleDefault: 'Teid\u00e4t on kutsuttu liittym\u00e4\u00e4n roolissa {{role}}',
            processing: 'K\u00e4sitell\u00e4\u00e4n kutsuanne\u2026',
            redirectingToLogin: 'Ohjataan kirjautumiseen\u2026',
            validating: 'Vahvistetaan kutsuanne\u2026',
            successAccepted: 'Kutsu hyv\u00e4ksytty!',
            successRegistered: 'Rekister\u00f6ityminen onnistui!',
            successJoined: 'Olette liittyneet organisaatioon {{organization}}.',
            redirecting: 'Ohjataan kirjautumiseen\u2026',
            errorTitle: 'Kutsuvirhe',
            errorInvalidToken: 'T\u00e4m\u00e4 kutsulinkki on virheellinen. Tarkistakaa linkki tai pyytt\u00e4k\u00e4\u00e4 uusi kutsu.',
            errorExpired: 'T\u00e4m\u00e4 kutsu on vanhentunut. Pyytt\u00e4k\u00e4\u00e4 uusi kutsu.',
            errorAlreadyAccepted: 'T\u00e4m\u00e4 kutsu on jo hyv\u00e4ksytty.',
            errorNetwork: 'Kutsun vahvistus ep\u00e4onnistui. Tarkistakaa yhteys ja yritt\u00e4k\u00e4\u00e4 uudelleen.',
            errorGeneric: 'T\u00e4m\u00e4 kutsu on virheellinen tai vanhentunut. Pyytt\u00e4k\u00e4\u00e4 uusi kutsu.',
            errorNoToken: 'Kutsutunnusta ei annettu. K\u00e4ytt\u00e4k\u00e4\u00e4 kutsus\u00e4hk\u00f6postin linkki\u00e4.',
            needHelp: 'Tarvitsetteko apua?',
            contactSupport: 'Ota yhteytt\u00e4 tukeen',
            wrongAccount: 'V\u00e4\u00e4r\u00e4 tili?',
            switchAccount: 'Kirjaudu toisella tilill\u00e4',
            hasAccount: 'Onko teill\u00e4 jo tili?',
            signInInstead: 'Kirjaudu sis\u00e4\u00e4n',
            email: 'S\u00e4hk\u00f6posti',
            verificationCode: 'Vahvistuskoodi',
            verificationCodePlaceholder: 'Sy\u00f6t\u00e4 6-numeroinen koodi',
            verificationCodeHint: 'Tarkistakaa s\u00e4hk\u00f6postinne 6-numeroisen vahvistuskoodin varalta',
            firstName: 'Etunimi',
            firstNamePlaceholder: 'Etunimi',
            lastName: 'Sukunimi',
            lastNamePlaceholder: 'Sukunimi',
            password: 'Salasana',
            passwordPlaceholder: 'Luokaa salasana',
            confirmPassword: 'Vahvistakaa salasana',
            confirmPasswordPlaceholder: 'Vahvistakaa salasananne',
            acceptButton: 'Hyv\u00e4ksy kutsu',
            accepting: 'Hyv\u00e4ksyt\u00e4\u00e4n\u2026',
            createButton: 'Luo tili ja liity',
            creating: 'Luodaan tili\u00e4\u2026',
            orSignInWith: 'Tai kirjaudu palvelulla',
            orContinueWith: 'Tai jatka palvelulla',
            termsNotice: 'Luomalla tilin hyv\u00e4ksytte',
            termsOfService: 'K\u00e4ytt\u00f6ehdot',
            privacyPolicy: 'Tietosuojak\u00e4yt\u00e4nt\u00f6',
            goToLogin: 'Siirry kirjautumiseen',
            validationCodeRequired: 'Sy\u00f6tt\u00e4k\u00e4\u00e4 6-numeroinen vahvistuskoodi kutsus\u00e4hk\u00f6postistanne.',
            validationNameRequired: 'Etunimi ja sukunimi ovat pakollisia.',
            validationPasswordMatch: 'Salasanat eiv\u00e4t t\u00e4sm\u00e4\u00e4.',
            acceptFailed: 'Kutsun hyv\u00e4ksyminen ep\u00e4onnistui. Tarkistakaa vahvistuskoodinne.',
            registrationFailed: 'Rekister\u00f6ityminen ep\u00e4onnistui. Tarkistakaa vahvistuskoodinne ja yritt\u00e4k\u00e4\u00e4 uudelleen.',
            didntReceiveCode: 'Etk\u00f6 saanut koodia?',
            resendButton: 'L\u00e4het\u00e4 koodi uudelleen',
            resendSuccess: 'Uusi vahvistuskoodi on l\u00e4hetetty s\u00e4hk\u00f6postiinne.',
            resendError: 'Koodin uudelleenl\u00e4hetys ep\u00e4onnistui. Yritt\u00e4k\u00e4\u00e4 uudelleen.',
            resendCooldown: 'L\u00e4het\u00e4 uudelleen {{seconds}} s kuluttua',
          },
          passwordReset: fiPasswordReset,
          newPassword: {
            hint: 'Vähintään {{min}} merkkiä.',
            hintWithNumberOrSpecial: 'Vähintään {{min}} merkkiä, joista yksi on numero tai erikoismerkki.',
          },
          logout: {
            signingOut: 'Kirjaudutaan ulos\u2026',
            error: 'Uloskirjautuminen ep\u00e4onnistui',
            redirecting: 'Ohjataan kirjautumiseen\u2026',
          },
        },
        common: {
          loading: 'Ladataan\u2026',
          error: 'Virhe',
          success: 'Onnistui',
          retry: 'Yrit\u00e4 uudelleen',
          cancel: 'Peruuta',
          continue: 'Jatka',
          back: 'Takaisin',
          next: 'Seuraava',
          finish: 'Valmis',
          close: 'Sulje',
          save: 'Tallenna',
          email: 'S\u00e4hk\u00f6posti',
          password: 'Salasana',
          name: 'Nimi',
          required: 'Pakollinen',
          optional: 'Valinnainen',
        },
        errors: {
          invalidCredentials: 'Virheellinen s\u00e4hk\u00f6postiosoite tai salasana',
          accountLocked: 'Tili lukittu. Yritt\u00e4k\u00e4\u00e4 my\u00f6hemmin uudelleen.',
          networkError: 'Verkkovirhe. Tarkistakaa yhteys.',
          sessionExpired: 'Istuntonne on vanhentunut. Kirjautukaa uudelleen.',
          invalidToken: 'Virheellinen tai vanhentunut tunnus',
          emailRequired: 'S\u00e4hk\u00f6postiosoite on pakollinen',
          passwordRequired: 'Salasana on pakollinen',
          passwordTooShort: 'Salasanan on oltava v\u00e4hint\u00e4\u00e4n 8 merkki\u00e4',
          passwordsDoNotMatch: 'Salasanat eiv\u00e4t t\u00e4sm\u00e4\u00e4',
          invalidEmail: 'Sy\u00f6tt\u00e4k\u00e4\u00e4 kelvollinen s\u00e4hk\u00f6postiosoite',
          termsRequired: 'Teid\u00e4n on hyv\u00e4ksytt\u00e4v\u00e4 k\u00e4ytt\u00f6ehdot',
          mfaRequired: 'Kaksivaiheinen tunnistautuminen vaaditaan',
          invalidMfaCode: 'Virheellinen vahvistuskoodi',
          rateLimited: 'Liian monta yrityst\u00e4. Yritt\u00e4k\u00e4\u00e4 my\u00f6hemmin uudelleen.',
          serverError: 'Palvelinvirhe. Yritt\u00e4k\u00e4\u00e4 my\u00f6hemmin uudelleen.',
          unknownError: 'Odottamaton virhe tapahtui',
        },
      },
      sv: {
        auth: {
          signInRecovery: svSignInRecovery,
          authErrors: svAuthErrors,
          login: {
            copyright: '© {{year}} Agion. Alla rättigheter förbehållna.',
            headline: 'Agenter, tryggt under din kontroll.',
            subline: 'Bygg och styr pålitliga agentsvärmar som förvandlar dina data till intelligens — i ditt val av moln, modeller och appar. Suveränitet, levererad.',
            sublineShort: 'Bygg och styr pålitliga agentsvärmar — i ditt moln, dina modeller och appar.',
            formHeading: 'Välkommen tillbaka',
            formSubheading: 'Logga in på din Agion-arbetsyta.',
            showPassword: 'Visa',
            hidePassword: 'Dölj',
            newToAgion: 'Ny på Agion?',
            joinWaitlist: 'Gå med i väntelistan',
            languageSwitch: 'Språk',
            title: 'Logga in p\u00e5 ditt konto',
            email: 'E-postadress',
            emailLabel: 'E-post',
            emailPlaceholder: 'du@exempel.se',
            password: 'L\u00f6senord',
            passwordLabel: 'L\u00f6senord',
            remember: 'Kom ih\u00e5g mig',
            submit: 'Logga in',
            forgotPassword: 'Glömt lösenordet?',
            createAccount: 'Skapa ditt konto',
            noAccount: 'Ny p\u00e5 Agion?',
            orContinueWith: 'eller fortsätt med',
            signingIn: 'Loggar in\u2026',
            googleSignIn: 'Logga in med Google',
            microsoftSignIn: 'Logga in med Microsoft',
            allRightsReserved: 'Alla r\u00e4ttigheter f\u00f6rbeh\u00e5llna.',
            missingRedirect: 'Omdirigeringsadress saknas i inloggningssvaret',
            genericError: 'Ett fel uppstod vid inloggningen',
            googleError: 'Det gick inte att starta Google-inloggningen',
            microsoftError: 'Det gick inte att starta Microsoft-inloggningen',
          },
          register: {
            title: 'Skapa ditt konto',
            firstName: 'F\u00f6rnamn',
            lastName: 'Efternamn',
            email: 'E-postadress',
            password: 'L\u00f6senord',
            confirmPassword: 'Bekr\u00e4fta l\u00f6senord',
            terms: 'Jag accepterar anv\u00e4ndarvillkoren och integritetspolicyn',
            submit: 'Skapa konto',
            alreadyHaveAccount: 'Har du redan ett konto?',
            signIn: 'Logga in',
            creating: 'Skapar konto\u2026',
            heading: 'Skapa konto',
            subheading: 'G\u00e5 med i Agion-plattformen i dag',
            goToLogin: 'G\u00e5 till inloggningssidan',
            firstNamePlaceholder: 'Erik',
            lastNamePlaceholder: 'Andersson',
            emailPlaceholder: 'erik@foretag.se',
            passwordPlaceholder: 'Skapa ett l\u00f6senord',
            confirmPasswordPlaceholder: 'Ange l\u00f6senordet igen',
            acceptTermsLead: 'Jag accepterar',
            termsOfService: 'anv\u00e4ndarvillkoren',
            acceptTermsJoin: 'och',
            privacyPolicy: 'integritetspolicyn',
            submitButton: 'Skapa konto',
            creatingButton: 'Skapar konto\u2026',
          },
          organizationSignup: {
            title: 'Skapa organisation',
            description: 'Din organisation finns inte \u00e4nnu. Skapa en f\u00f6r att forts\u00e4tta.',
            namePlaceholder: 'Acme AB',
            creating: 'Skapar organisation\u2026',
            submit: 'Skapa organisation',
            backToLogin: 'Tillbaka till inloggningen',
          },
          mfa: {
            title: 'Tv\u00e5faktorsautentisering',
            description: 'Ange verifieringskoden fr\u00e5n din autentiseringsapp',
            code: 'Verifieringskod',
            submit: 'Verifiera',
            backupCode: 'Anv\u00e4nd reservkod',
            havingTrouble: 'Problem?',
            verifying: 'Verifierar\u2026',
            methods: {
              totp: 'Autentiseringsapp',
              webauthn: 'S\u00e4kerhetsnyckel',
              sms: 'SMS',
              email: 'E-post',
              backup: 'Reservkod',
            },
          },
          consent: {
            title: 'Auktorisera {{client}}',
            description: '{{client}} beg\u00e4r \u00e5tkomst till ditt konto',
            defaultDescription: 'Denna applikation beg\u00e4r \u00e5tkomst till ditt konto',
            permissions: 'Denna applikation kommer att kunna:',
            permissionsTitle: 'Denna applikation kommer att kunna:',
            userInfo: 'Delad information:',
            remember: 'Kom ih\u00e5g mitt val',
            allow: 'Till\u00e5t',
            deny: 'Neka',
            allowing: 'Auktoriserar\u2026',
            invalid: 'Ogiltig samtyckesf\u00f6rfr\u00e5gan',
            invalidDescription: 'Samtyckesf\u00f6rfr\u00e5gan \u00e4r ogiltig eller har g\u00e5tt ut.',
            required: 'Obligatorisk',
            loggedInAs: 'Inloggad som {{email}}',
            website: 'Webbplats',
            privacy: 'Integritetspolicy',
            terms: 'Anv\u00e4ndarvillkor',
            scopes: {
              openid: 'Verifiera din identitet',
              openidDesc: '\u00c5tkomst till din unika anv\u00e4ndaridentifierare',
              profile: 'Visa din profil',
              profileDesc: '\u00c5tkomst till ditt namn och din profilinformation',
              email: 'Visa din e-postadress',
              emailDesc: '\u00c5tkomst till din e-postadress',
              phone: 'Visa ditt telefonnummer',
              phoneDesc: '\u00c5tkomst till ditt telefonnummer',
              address: 'Visa din adress',
              addressDesc: '\u00c5tkomst till din postadress',
              offlineAccess: 'Fortsatt inloggning',
              offlineAccessDesc: 'Beh\u00e5lla \u00e5tkomst n\u00e4r du inte aktivt anv\u00e4nder applikationen',
              customDesc: '\u00c5tkomst till {{scope}}',
            },
          },
          forgotPassword: {
            title: '\u00c5terst\u00e4ll ditt l\u00f6senord',
            description: 'Ange din e-postadress s\u00e5 skickar vi en l\u00e4nk f\u00f6r att \u00e5terst\u00e4lla ditt l\u00f6senord',
            email: 'E-postadress',
            submit: 'Skicka \u00e5terst\u00e4llningsl\u00e4nk',
            backToLogin: 'Tillbaka till inloggningen',
            sending: 'Skickar\u2026',
          },
          resetPassword: {
            title: 'Ange nytt l\u00f6senord',
            description: 'Ange ditt nya l\u00f6senord nedan',
            password: 'Nytt l\u00f6senord',
            confirmPassword: 'Bekr\u00e4fta nytt l\u00f6senord',
            submit: '\u00c5terst\u00e4ll l\u00f6senord',
            resetting: '\u00c5terst\u00e4ller\u2026',
          },
          emailVerification: {
            title: 'Kontrollera din e-post',
            description: 'Vi har skickat en verifieringsl\u00e4nk till din e-postadress. Klicka p\u00e5 l\u00e4nken f\u00f6r att verifiera ditt konto och forts\u00e4tta.',
            descriptionWithEmail: 'Vi har skickat en verifieringsl\u00e4nk till {{email}}. Klicka p\u00e5 l\u00e4nken f\u00f6r att verifiera ditt konto och forts\u00e4tta.',
            expiryWarning: 'Verifieringsl\u00e4nken g\u00e5r ut om 30 minuter.',
            checkSpam: 'Ser du inte e-postmeddelandet? Kontrollera din skr\u00e4ppost.',
            didntReceive: 'Fick du inget e-postmeddelande?',
            resendButton: 'Skicka verifieringsmeddelande igen',
            resendSuccess: 'Verifieringsmeddelande skickat! Den nya l\u00e4nken g\u00e5r ut om 30 minuter.',
            resendError: 'Det gick inte att skicka verifieringsmeddelandet igen',
            successTitle: 'E-post verifierad!',
            successMessage: 'Din e-postadress har verifierats.',
            verifiedDomain: 'Verifierad dom\u00e4n',
            signInToCreateOrg: 'Logga in f\u00f6r att konfigurera din organisation.',
            signIn: 'Logga in',
          },
          device: {
            title: 'Enhetsauktorisering',
            description: 'Ange koden som visas p\u00e5 din enhet f\u00f6r att logga in.',
            codeLabel: 'Enhetskod',
            codeHint: 'Ange koden som visas i din terminal',
            continue: 'Forts\u00e4tt',
            verifying: 'Verifierar kod\u2026',
            confirmTitle: 'Auktorisera enhet',
            confirmDescription: 'En enhet beg\u00e4r \u00e5tkomst till ditt konto.',
            requestingApp: 'Applikation',
            permissions: 'Beg\u00e4rda beh\u00f6righeter',
            approve: 'Godk\u00e4nn',
            deny: 'Neka',
            securityWarning: 'Godk\u00e4nn endast om du initierade denna f\u00f6rfr\u00e5gan.',
            successTitle: 'Enhet auktoriserad',
            successDescription: 'Din enhet har auktoriserats. Du kan \u00e5terg\u00e5 till din terminal.',
            closeWindow: 'Du kan st\u00e4nga detta f\u00f6nster.',
            deniedTitle: 'Auktorisering nekad',
            deniedDescription: 'Enhetsauktoriseringsf\u00f6rfr\u00e5gan nekades.',
            errorTitle: 'Fel',
            genericError: 'N\u00e5got gick fel. F\u00f6rs\u00f6k igen.',
            tryAgain: 'F\u00f6rs\u00f6k igen',
            invalidCode: 'Ogiltig eller utg\u00e5ngen kod. Kontrollera och f\u00f6rs\u00f6k igen.',
            verifyError: 'Det gick inte att verifiera koden. F\u00f6rs\u00f6k igen.',
            approveError: 'Det gick inte att auktorisera enheten. F\u00f6rs\u00f6k igen.',
            denyError: 'Det gick inte att neka f\u00f6rfr\u00e5gan. F\u00f6rs\u00f6k igen.',
            scopes: {
              openid: 'Verifiera din identitet',
              profile: 'Visa ditt namn och din profil',
              email: 'Visa din e-postadress',
              offlineAccess: 'Fortsatt inloggning',
            },
          },
          authorize: {
            title: 'Auktoriseringsf\u00f6rfr\u00e5gan',
            description: '{{app}} beg\u00e4r \u00e5tkomst till ditt konto',
            authorizing: 'Auktoriserar\u2026',
            errorTitle: 'Auktoriseringsfel',
            invalidRequest: 'Ogiltig auktoriseringsf\u00f6rfr\u00e5gan. Obligatoriska parametrar saknas.',
            invalidRedirectUri: 'Ogiltig omdirigerings-URI f\u00f6r denna klient.',
            processingError: 'Det gick inte att behandla auktoriseringsf\u00f6rfr\u00e5gan.',
            authorizationFailed: 'Auktoriseringen misslyckades',
            invalidServerResponse: 'Ogiltigt svar fr\u00e5n auktoriseringsservern',
            redirectMismatch: 'Ogiltig omdirigerings-URL fr\u00e5n servern \u2014 matchar inte registrerad redirect_uri',
            permissionsTitle: 'Denna applikation kommer att kunna:',
            secureTitle: 'S\u00e4ker auktorisering',
            secureDescription: 'Denna auktorisering anv\u00e4nder branschstandarden OAuth 2.0 med PKCE f\u00f6r f\u00f6rb\u00e4ttrad s\u00e4kerhet.',
            approve: 'Auktorisera',
            deny: 'Neka',
            defaultUser: 'Anv\u00e4ndare',
            footerConsent: 'Genom att auktorisera till\u00e5ter du {{app}} att f\u00e5 \u00e5tkomst till ditt Agion-konto \u00e5 dina v\u00e4gnar.',
            footerRevoke: 'Du kan \u00e5terkalla denna \u00e5tkomst n\u00e4r som helst fr\u00e5n dina kontoinst\u00e4llningar.',
          },
          layout: {
            privacyPolicy: 'Integritetspolicy',
            termsOfService: 'Anv\u00e4ndarvillkor',
            support: 'Support',
            skipToContent: 'G\u00e5 till huvudinneh\u00e5llet',
            allRightsReserved: 'Alla r\u00e4ttigheter f\u00f6rbeh\u00e5llna.',
          },
          backToLogin: 'Tillbaka till inloggningen',
          waitlist: {
            checkEmailTitle: 'Kolla din e-post',
            checkEmailExpiry: 'Länken går ut efter 24 timmar.',
            signedInAs: 'Inloggad som',
            loading: 'Laddar',
            emailInvalidWork: 'Ange en giltig arbetsmejl.',
            requestAccessTitle: 'Begär åtkomst',
            requestAccessSubtitle: 'Agion introducerar organisationer i vågor. Berätta var du arbetar så hör vi av oss.',
            requestAccessButton: 'Begär åtkomst',
            orRequestWith: 'eller begär åtkomst med',
            oneMoreStepTitle: 'Ett steg till',
            oneMoreStepSubtitle: 'Lägg till din organisations namn för att slutföra och fortsätt sedan med den leverantör du använde.',
            continueWithGoogle: 'Fortsätt med Google',
            continueWithMicrosoft: 'Fortsätt med Microsoft',
            checkEmailMessageRich: 'Vi skickade en verifieringslänk och en kod till <b>{{email}}</b>. Klicka på länken eller ange koden nedan.',
            codeLabel: 'Verifieringskod',
            codePlaceholder: '123456',
            codeHint: 'Ange den 6-siffriga koden från e-postmeddelandet. Den går ut efter 15 minuter.',
            codeSubmit: 'Fortsätt',
            codeInvalid: 'Koden är ogiltig eller har gått ut. Kontrollera e-postmeddelandet eller begär en ny.',
            didntGetEmail: 'Fick du inte e-postmeddelandet?',
            onListTitle: 'Du är med på listan',
            onListMessage: 'Tack för ditt intresse för Agion. Vi mejlar dig så snart åtkomsten öppnas för din organisation.',
            onListInbox: 'Håll koll på din inkorg för uppdateringar.',
            setPasswordTitle: 'Ange ditt lösenord',
            setPasswordSubtitle: 'Bekräfta dina uppgifter och välj ett lösenord för att slutföra anslutningen.',
            workEmailLabel: 'Arbetsmejl',
            verifiedMarker: 'Verifierad',
            passwordWeak: 'Svagt lösenord',
            passwordGood: 'Bra lösenord',
            passwordStrong: 'Starkt lösenord',
            showPassword: 'Visa',
            hidePassword: 'Dölj',
            passwordsDontMatch: 'Lösenorden matchar inte.',
            passwordsMatch: 'Lösenorden matchar',
            confirmJoinButton: 'Bekräfta och gå med',
            pendingApprovalTitle: 'Begäran väntar på godkännande',
            pendingApprovalBodyRich: 'Din organisation finns redan på Agion. Vi har skickat din begäran om att gå med i <b>{{organization}}</b> till dess administratörer.',
            pendingApprovalBodyGeneric: 'Din organisation finns redan på Agion. Vi har skickat din begäran om att gå med till dess administratörer.',
            whatHappensNext: 'Vad händer härnäst',
            pendingStep1: 'En administratör granskar din begäran om att gå med',
            pendingStep2: 'Du godkänns och läggs till i arbetsytan',
            pendingStep3: 'Du får ett mejl — sedan kan du logga in',
            wrongOrg: 'Fel organisation? Kontakta',
            backToSignIn: 'Tillbaka till inloggning',
            title: 'G\u00e5 med i Agions v\u00e4ntelista',
            subtitle: 'K\u00f6r sv\u00e4rmar av AI-agenter s\u00e4kert. Skapa ett konto f\u00f6r att g\u00e5 med i v\u00e5r v\u00e4ntelista idag.',
            emailLabel: 'Arbets-e-post',
            emailPlaceholder: 'du@foretag.se',
            orgLabel: 'Organisationsnamn',
            orgPlaceholder: 'Ditt f\u00f6retag eller din organisation',
            joinButton: 'G\u00e5 med i v\u00e4ntelistan',
            submitting: 'Skickar\u2026',
            orSignUpWith: 'eller registrera dig med',
            googleSignUp: 'Registrera dig med Google',
            microsoftSignUp: 'Registrera dig med Microsoft',
            hasAccount: 'Har du redan ett konto?',
            signIn: 'Logga in',
            blockedDomain: 'Anv\u00e4nd din arbets-e-postadress',
            emailRequired: 'E-postadress kr\u00e4vs',
            emailInvalid: 'Ange en giltig e-postadress',
            orgRequired: 'Organisationsnamn kr\u00e4vs',
            genericError: 'Ett fel uppstod',
            successTitle: 'Tack!',
            successMessage: 'Vi har tagit emot ditt intresse f\u00f6r Agion. Vi h\u00f6r av oss n\u00e4r \u00e5tkomst blir tillg\u00e4nglig f\u00f6r din organisation.',
            successInbox: 'Kontrollera din inkorg f\u00f6r uppdateringar.',
            copyright: 'Agion. Alla r\u00e4ttigheter f\u00f6rbeh\u00e5llna.',
            confirmTitle: 'Bekr\u00e4fta dina uppgifter',
            confirmSubtitle: 'Kontrollera dina uppgifter innan du g\u00e5r med i v\u00e4ntelistan.',
            confirmButton: 'Bekr\u00e4fta och g\u00e5 med i v\u00e4ntelistan',
            passwordLabel: 'L\u00f6senord',
            passwordPlaceholder: 'Skapa ett l\u00f6senord',
            confirmPasswordLabel: 'Bekr\u00e4fta l\u00f6senord',
            confirmPasswordPlaceholder: 'Ange l\u00f6senordet igen',
            passwordRequired: 'L\u00f6senord kr\u00e4vs',
            passwordMismatch: 'L\u00f6senorden matchar inte',
            linkExpiredTitle: 'Länken har upphört',
            backToWaitlist: 'Tillbaka till åtkomstbegäran',
            invalidRequest: 'Ogiltig begäran. Försök igen.',
            tokenExpired: 'Ogiltig eller utg\u00e5ngen token',
            didntReceiveEmail: 'Fick du inget e-postmeddelande?',
            resendButton: 'Skicka verifieringsmeddelande igen',
            resendSuccess: 'Verifieringsmeddelande skickat! Kontrollera din inkorg.',
            resendCooldown: 'Skicka igen om {{seconds}}s',
            pendingTitle: 'Ditt konto v\u00e4ntar p\u00e5 godk\u00e4nnande',
            pendingMessage: 'Vi meddelar dig via e-post när din organisation är klar. Du kan logga in med samma konto som du använde för att gå med i väntelistan.',
            pendingContact: 'Fr\u00e5gor? Kontakta oss p\u00e5',
            supportEmail: 'support@agion.ai',
          },
          socialLogin: {
            google: 'Forts\u00e4tt med Google',
            github: 'Forts\u00e4tt med GitHub',
            microsoft: 'Forts\u00e4tt med Microsoft',
            apple: 'Forts\u00e4tt med Apple',
          },
          social: {
            google: 'Google',
            microsoft: 'Microsoft',
          },
          invite: {
            title: 'G\u00e5 med i {{organization}}',
            titleDefault: 'Organisationsinbjudan',
            subtitle: '{{inviter}} har bjudit in dig att g\u00e5 med som {{role}}',
            subtitleDefault: 'Du har blivit inbjuden att g\u00e5 med som {{role}}',
            processing: 'Behandlar din inbjudan\u2026',
            redirectingToLogin: 'Omdirigerar till inloggning\u2026',
            validating: 'Validerar din inbjudan\u2026',
            successAccepted: 'Inbjudan accepterad!',
            successRegistered: 'Registrering lyckades!',
            successJoined: 'Du har g\u00e5tt med i {{organization}}.',
            redirecting: 'Omdirigerar till inloggning\u2026',
            errorTitle: 'Inbjudningsfel',
            errorInvalidToken: 'Denna inbjudningsl\u00e4nk \u00e4r ogiltig. Kontrollera l\u00e4nken eller beg\u00e4r en ny inbjudan.',
            errorExpired: 'Denna inbjudan har g\u00e5tt ut. Beg\u00e4r en ny inbjudan.',
            errorAlreadyAccepted: 'Denna inbjudan har redan accepterats.',
            errorNetwork: 'Det gick inte att validera inbjudan. Kontrollera din anslutning och f\u00f6rs\u00f6k igen.',
            errorGeneric: 'Denna inbjudan \u00e4r ogiltig eller har g\u00e5tt ut. Beg\u00e4r en ny inbjudan.',
            errorNoToken: 'Ingen inbjudningstoken angavs. Anv\u00e4nd l\u00e4nken fr\u00e5n ditt inbjudningsmeddelande.',
            needHelp: 'Beh\u00f6ver du hj\u00e4lp?',
            contactSupport: 'Kontakta support',
            wrongAccount: 'Fel konto?',
            switchAccount: 'Logga in med ett annat konto',
            hasAccount: 'Har du redan ett konto?',
            signInInstead: 'Logga in ist\u00e4llet',
            email: 'E-post',
            verificationCode: 'Verifieringskod',
            verificationCodePlaceholder: 'Ange 6-siffrig kod',
            verificationCodeHint: 'Kontrollera din e-post f\u00f6r den 6-siffriga verifieringskoden',
            firstName: 'F\u00f6rnamn',
            firstNamePlaceholder: 'F\u00f6rnamn',
            lastName: 'Efternamn',
            lastNamePlaceholder: 'Efternamn',
            password: 'L\u00f6senord',
            passwordPlaceholder: 'Skapa ett l\u00f6senord',
            confirmPassword: 'Bekr\u00e4fta l\u00f6senord',
            confirmPasswordPlaceholder: 'Bekr\u00e4fta ditt l\u00f6senord',
            acceptButton: 'Acceptera inbjudan',
            accepting: 'Accepterar\u2026',
            createButton: 'Skapa konto och g\u00e5 med',
            creating: 'Skapar konto\u2026',
            orSignInWith: 'Eller logga in med',
            orContinueWith: 'Eller forts\u00e4tt med',
            termsNotice: 'Genom att skapa ett konto godk\u00e4nner du v\u00e5ra',
            termsOfService: 'Anv\u00e4ndarvillkor',
            privacyPolicy: 'Integritetspolicy',
            goToLogin: 'G\u00e5 till inloggning',
            validationCodeRequired: 'Ange den 6-siffriga verifieringskoden fr\u00e5n ditt inbjudningsmeddelande.',
            validationNameRequired: 'F\u00f6rnamn och efternamn kr\u00e4vs.',
            validationPasswordMatch: 'L\u00f6senorden matchar inte.',
            acceptFailed: 'Det gick inte att acceptera inbjudan. Kontrollera din verifieringskod.',
            registrationFailed: 'Registreringen misslyckades. Kontrollera din verifieringskod och f\u00f6rs\u00f6k igen.',
            didntReceiveCode: 'Fick du ingen kod?',
            resendButton: 'Skicka kod igen',
            resendSuccess: 'En ny verifieringskod har skickats till din e-post.',
            resendError: 'Det gick inte att skicka koden igen. F\u00f6rs\u00f6k igen.',
            resendCooldown: 'Skicka igen om {{seconds}}s',
          },
          passwordReset: svPasswordReset,
          newPassword: {
            hint: 'Minst {{min}} tecken.',
            hintWithNumberOrSpecial: 'Minst {{min}} tecken, varav minst en siffra eller ett specialtecken.',
          },
          logout: {
            signingOut: 'Loggar ut\u2026',
            error: 'Utloggningen misslyckades',
            redirecting: 'Omdirigerar till inloggning\u2026',
          },
        },
        common: {
          loading: 'Laddar\u2026',
          error: 'Fel',
          success: 'Lyckades',
          retry: 'F\u00f6rs\u00f6k igen',
          cancel: 'Avbryt',
          continue: 'Forts\u00e4tt',
          back: 'Tillbaka',
          next: 'N\u00e4sta',
          finish: 'Slutf\u00f6r',
          close: 'St\u00e4ng',
          save: 'Spara',
          email: 'E-post',
          password: 'L\u00f6senord',
          name: 'Namn',
          required: 'Obligatorisk',
          optional: 'Valfri',
        },
        errors: {
          invalidCredentials: 'Ogiltig e-postadress eller l\u00f6senord',
          accountLocked: 'Kontot \u00e4r l\u00e5st. F\u00f6rs\u00f6k igen senare.',
          networkError: 'N\u00e4tverksfel. Kontrollera din anslutning.',
          sessionExpired: 'Din session har g\u00e5tt ut. Logga in igen.',
          invalidToken: 'Ogiltig eller utg\u00e5ngen token',
          emailRequired: 'E-postadress kr\u00e4vs',
          passwordRequired: 'L\u00f6senord kr\u00e4vs',
          passwordTooShort: 'L\u00f6senordet m\u00e5ste vara minst 8 tecken',
          passwordsDoNotMatch: 'L\u00f6senorden matchar inte',
          invalidEmail: 'Ange en giltig e-postadress',
          termsRequired: 'Du m\u00e5ste acceptera villkoren',
          mfaRequired: 'Tv\u00e5faktorsautentisering kr\u00e4vs',
          invalidMfaCode: 'Ogiltig verifieringskod',
          rateLimited: 'F\u00f6r m\u00e5nga f\u00f6rs\u00f6k. F\u00f6rs\u00f6k igen senare.',
          serverError: 'Serverfel. F\u00f6rs\u00f6k igen senare.',
          unknownError: 'Ett ov\u00e4ntat fel uppstod',
        },
      },
    },
  });

export default i18n;
